/**
 * Fórmula de cálculo e variáveis do contrato — `2j`, decidido por Fábio em 17/09/2026.
 *
 * 🔒 **O servidor é autoritativo.** `gestao_orcamento` valida a mesma gramática
 * (`formula_calculo.py`) e recusa com 422. Este módulo existe para (1) a tela dizer
 * O QUE está errado antes de ir ao servidor, (2) o MSW recusar exatamente como o
 * backend recusa e (3) os testes fixarem a regra num lugar só.
 *
 * ⛔ Nada aqui AVALIA a fórmula. Não há `eval`, não há aritmética: calcular custo é
 * do backend, com os parâmetros vigentes no período (BR-21). Isto só responde "a
 * fórmula é bem formada e usa apenas variáveis declaradas?".
 *
 * Gramática (sem operador unário — `-1` precisa ser escrito `(0 - 1)`):
 *
 * ```
 * expressao := operando (operador operando)*
 * operando  := numero | tag | "(" expressao ")"
 * operador  := "+" | "-" | "*" | "/"
 * numero    := [0-9]+ ("." [0-9]+)?
 * tag       := [a-z][a-z0-9_]*   — e declarada em `variaveis`
 * ```
 *
 * O formato serializado é o mesmo que as fixtures sempre usaram:
 * `(consumo_usd * cotacao_ptax) * (1 - desconto_aws / 100)`.
 */
import { TAG_COTACAO_PTAX, TAG_DESCONTO_AWS } from '@/api/contracts/contrato'
import type { VariavelDeContrato } from '@/api/contracts/contrato'

export const PADRAO_DE_TAG = /^[a-z][a-z0-9_]*$/

// ── Tokens ──────────────────────────────────────────────────────────────────

export type Token =
  | { tipo: 'numero'; texto: string; posicao: number }
  | { tipo: 'identificador'; texto: string; posicao: number }
  | { tipo: 'operador'; texto: '+' | '-' | '*' | '/'; posicao: number }
  | { tipo: 'abre'; texto: '('; posicao: number }
  | { tipo: 'fecha'; texto: ')'; posicao: number }

export type ResultadoDaValidacao =
  | { ok: true }
  | {
      ok: false
      mensagem: string
      /** Índice (base 0) do caractere problemático na fórmula serializada. */
      posicao: number
    }

type Tokenizacao = { ok: true; tokens: Token[] } | Extract<ResultadoDaValidacao, { ok: false }>

/**
 * Quebra a fórmula em tokens. Identificadores aceitam maiúscula AQUI de propósito:
 * `Consumo_USD` vira um identificador que a validação recusa como "não declarado",
 * o que diz mais do que "caractere inválido".
 */
export function tokenizar(formula: string): Tokenizacao {
  const tokens: Token[] = []
  let i = 0
  while (i < formula.length) {
    const c = formula[i]!
    if (/\s/.test(c)) {
      i++
      continue
    }
    if (c === '(') {
      tokens.push({ tipo: 'abre', texto: '(', posicao: i++ })
      continue
    }
    if (c === ')') {
      tokens.push({ tipo: 'fecha', texto: ')', posicao: i++ })
      continue
    }
    if (c === '+' || c === '-' || c === '*' || c === '/') {
      tokens.push({ tipo: 'operador', texto: c, posicao: i++ })
      continue
    }
    const numero = /^\d+(?:\.\d+)?/.exec(formula.slice(i))
    if (numero) {
      const seguinte = formula[i + numero[0].length]
      if (seguinte === '.' || seguinte === ',') {
        return {
          ok: false,
          mensagem: `Número mal escrito perto de "${formula.slice(i, i + numero[0].length + 2)}" — use ponto como separador decimal, ex. 0.15.`,
          posicao: i,
        }
      }
      tokens.push({ tipo: 'numero', texto: numero[0], posicao: i })
      i += numero[0].length
      continue
    }
    const identificador = /^[A-Za-z_][A-Za-z0-9_]*/.exec(formula.slice(i))
    if (identificador) {
      tokens.push({ tipo: 'identificador', texto: identificador[0], posicao: i })
      i += identificador[0].length
      continue
    }
    return {
      ok: false,
      mensagem:
        c === ','
          ? 'Vírgula não é permitida na fórmula — use ponto como separador decimal, ex. 0.15.'
          : `Caractere não permitido na fórmula: "${c}". Use apenas variáveis, números, parênteses e + - * /.`,
      posicao: i,
    }
  }
  return { ok: true, tokens }
}

/** Valida a fórmula serializada contra o conjunto de tags declaradas. */
export function validarFormula(formula: string, tags: Iterable<string>): ResultadoDaValidacao {
  const tokenizacao = tokenizar(formula)
  if (!tokenizacao.ok) return tokenizacao
  const { tokens } = tokenizacao

  if (tokens.length === 0) {
    return { ok: false, mensagem: 'A fórmula de cálculo está vazia.', posicao: 0 }
  }

  const declaradas = new Set(tags)
  const abertos: Token[] = []
  let esperaOperando = true
  let anterior: Token | undefined

  for (const token of tokens) {
    if (token.tipo === 'identificador' && !declaradas.has(token.texto)) {
      return {
        ok: false,
        mensagem: `"${token.texto}" não é uma variável declarada no contrato.`,
        posicao: token.posicao,
      }
    }

    if (esperaOperando) {
      if (token.tipo === 'abre') {
        abertos.push(token)
      } else if (token.tipo === 'numero' || token.tipo === 'identificador') {
        esperaOperando = false
      } else {
        return {
          ok: false,
          mensagem: anterior
            ? `Falta uma variável ou número entre "${anterior.texto}" e "${token.texto}".`
            : `A fórmula não pode começar com "${token.texto}".`,
          posicao: token.posicao,
        }
      }
    } else if (token.tipo === 'operador') {
      esperaOperando = true
    } else if (token.tipo === 'fecha') {
      if (!abertos.pop()) {
        return {
          ok: false,
          mensagem: 'Há um ")" sem o "(" correspondente.',
          posicao: token.posicao,
        }
      }
    } else {
      return {
        ok: false,
        mensagem: `Falta um operador (+ - * /) entre "${anterior!.texto}" e "${token.texto}".`,
        posicao: token.posicao,
      }
    }
    anterior = token
  }

  if (esperaOperando) {
    return {
      ok: false,
      mensagem: `A fórmula não pode terminar em "${anterior!.texto}".`,
      posicao: anterior!.posicao,
    }
  }
  if (abertos.length > 0) {
    return {
      ok: false,
      mensagem:
        abertos.length === 1
          ? 'Falta fechar 1 parêntese "(".'
          : `Faltam fechar ${abertos.length} parênteses "(".`,
      posicao: abertos[abertos.length - 1]!.posicao,
    }
  }
  return { ok: true }
}

/** As tags que a fórmula usa — para bloquear a remoção de uma variável em uso. */
export function tagsUsadas(formula: string): Set<string> {
  const tokenizacao = tokenizar(formula)
  if (!tokenizacao.ok) return new Set()
  return new Set(tokenizacao.tokens.filter((t) => t.tipo === 'identificador').map((t) => t.texto))
}

// ── Variáveis ───────────────────────────────────────────────────────────────

/**
 * As tags cujo valor o BACKEND fornece no cálculo — as únicas que podem ficar
 * sem valor. Decidido por Fábio em 17/09/2026.
 *
 * ⚠️ **`consumo_usd` é a única porque é a única que sabemos existir**, e isso é
 * uma suposição registrada no §9, não um fato confirmado pela iOX: nada calcula
 * ainda. Uma variável sem valor que o backend NÃO fornece nunca seria preenchida
 * por ninguém — o cálculo simplesmente não teria o número. Por isso a exigência é
 * o padrão e a isenção é a lista, e não o contrário.
 *
 * Quando a iOX definir outras variáveis calculadas, ACRESCENTE a tag aqui — é o
 * único lugar (o backend espelha esta lista em `formula_calculo.py`).
 */
export const TAGS_FORNECIDAS_PELO_BACKEND: ReadonlySet<string> = new Set(['consumo_usd'])

/**
 * Os limites que o schema antigo garantia e que Fábio mandou preservar quando
 * `cotacaoPtax` e `descontoAwsPercentual` viraram linhas da tabela (17/09/2026):
 * `z.number().positive()` e `z.number().min(0).max(100)`.
 *
 * Por tag, e não por posição, porque agora são variáveis como quaisquer outras —
 * o que as distingue é o nome que o backend lê.
 */
export const LIMITES_POR_TAG: Readonly<
  Record<string, { minimo: number; maximo?: number; exclusivo?: boolean; comoDizer: string }>
> = {
  [TAG_COTACAO_PTAX]: {
    minimo: 0,
    exclusivo: true,
    comoDizer: 'A cotação PTAX deve ser maior que zero.',
  },
  [TAG_DESCONTO_AWS]: {
    minimo: 0,
    maximo: 100,
    comoDizer: 'O desconto AWS deve estar entre 0 e 100 (pontos percentuais).',
  },
}

/** Valida UMA variável contra as outras já declaradas. `null` = válida. */
export function validarVariavel(
  variavel: VariavelDeContrato,
  outrasTags: Iterable<string>,
): string | null {
  if (!variavel.tag) return 'Informe a tag da variável.'
  if (!PADRAO_DE_TAG.test(variavel.tag)) {
    return `A tag "${variavel.tag}" é inválida — use letras minúsculas, números e "_", começando por letra (ex. cotacao_ptax).`
  }
  if (new Set(outrasTags).has(variavel.tag)) {
    return `Já existe uma variável com a tag "${variavel.tag}".`
  }
  if (!variavel.nome.trim()) return `Informe o nome da variável "${variavel.tag}".`
  if (variavel.valor === null) {
    if (!TAGS_FORNECIDAS_PELO_BACKEND.has(variavel.tag)) {
      return `Informe o valor de "${variavel.tag}" — só ${[...TAGS_FORNECIDAS_PELO_BACKEND].join(', ')} é fornecida pelo backend no cálculo.`
    }
    return null
  }
  if (!Number.isFinite(variavel.valor)) {
    return `O valor de "${variavel.tag}" não é um número.`
  }
  const limite = LIMITES_POR_TAG[variavel.tag]
  if (limite) {
    const abaixo = limite.exclusivo
      ? variavel.valor <= limite.minimo
      : variavel.valor < limite.minimo
    if (abaixo || (limite.maximo !== undefined && variavel.valor > limite.maximo)) {
      return limite.comoDizer
    }
  }
  return null
}

/** Valida o conjunto inteiro, na ordem. Primeira falha vence. */
export function validarVariaveis(variaveis: readonly VariavelDeContrato[]): string | null {
  for (let i = 0; i < variaveis.length; i++) {
    const outras = variaveis.slice(0, i).map((v) => v.tag)
    const erro = validarVariavel(variaveis[i]!, outras)
    if (erro) return erro
  }
  return null
}

// ── Segmentos do editor ─────────────────────────────────────────────────────

/**
 * O modelo do editor composto: trechos de texto livre intercalados com chips de
 * variável. Sempre começa e termina em `texto` e nunca tem dois textos seguidos —
 * é o que dá um `<input>` antes, entre e depois de cada chip.
 */
export type SegmentoDeFormula = { tipo: 'texto'; valor: string } | { tipo: 'variavel'; tag: string }

/** Garante a alternância texto/variável/texto. */
function normalizar(segmentos: readonly SegmentoDeFormula[]): SegmentoDeFormula[] {
  const saida: SegmentoDeFormula[] = []
  for (const segmento of segmentos) {
    const ultimo = saida[saida.length - 1]
    if (segmento.tipo === 'texto') {
      if (ultimo?.tipo === 'texto') ultimo.valor += segmento.valor
      else saida.push({ ...segmento })
    } else {
      if (ultimo?.tipo !== 'texto') saida.push({ tipo: 'texto', valor: '' })
      saida.push({ ...segmento })
    }
  }
  if (saida[saida.length - 1]?.tipo !== 'texto') saida.push({ tipo: 'texto', valor: '' })
  return saida
}

/**
 * String → segmentos. Só vira chip o identificador DECLARADO; qualquer outro fica
 * no texto, onde a validação o aponta ("não é uma variável declarada").
 */
export function segmentar(formula: string, tags: Iterable<string>): SegmentoDeFormula[] {
  const declaradas = new Set(tags)
  const segmentos: SegmentoDeFormula[] = []
  const padrao = /[A-Za-z_][A-Za-z0-9_]*/g
  let inicio = 0
  for (const casado of formula.matchAll(padrao)) {
    const indice = casado.index
    // Um identificador colado num número (`2x`) não é chip: fica como texto.
    const antes = formula[indice - 1]
    if (!declaradas.has(casado[0]) || (antes !== undefined && /[\d.]/.test(antes))) continue
    segmentos.push({ tipo: 'texto', valor: formula.slice(inicio, indice) })
    segmentos.push({ tipo: 'variavel', tag: casado[0] })
    inicio = indice + casado[0].length
  }
  segmentos.push({ tipo: 'texto', valor: formula.slice(inicio) })
  return normalizar(segmentos)
}

/**
 * Segmentos → string. Um espaço é inserido só onde a colagem mudaria o sentido
 * (`2` + `consumo_usd`, ou dois chips seguidos) — o resto sai verbatim, e por isso
 * `serializar(segmentar(f))` devolve `f` para as fórmulas das fixtures.
 */
export function serializar(segmentos: readonly SegmentoDeFormula[]): string {
  let saida = ''
  for (const segmento of segmentos) {
    const pedaco = segmento.tipo === 'texto' ? segmento.valor : segmento.tag
    if (!pedaco) continue
    if (saida && /[\w.]$/.test(saida) && /^[\w.]/.test(pedaco)) saida += ' '
    saida += pedaco
  }
  return saida
}

/** Insere um chip no trecho `indiceTexto`, na posição `cursor` do texto dele. */
export function inserirVariavel(
  segmentos: readonly SegmentoDeFormula[],
  tag: string,
  indiceTexto?: number,
  cursor?: number,
): SegmentoDeFormula[] {
  const alvo =
    indiceTexto !== undefined && segmentos[indiceTexto]?.tipo === 'texto'
      ? indiceTexto
      : segmentos.length - 1
  const texto = segmentos[alvo]
  if (texto?.tipo !== 'texto') return normalizar([...segmentos, { tipo: 'variavel', tag }])
  const corte = Math.max(0, Math.min(cursor ?? texto.valor.length, texto.valor.length))
  return normalizar([
    ...segmentos.slice(0, alvo),
    { tipo: 'texto', valor: texto.valor.slice(0, corte) },
    { tipo: 'variavel', tag },
    { tipo: 'texto', valor: texto.valor.slice(corte) },
    ...segmentos.slice(alvo + 1),
  ])
}

/** Remove o chip do índice dado, juntando os textos vizinhos. */
export function removerSegmento(
  segmentos: readonly SegmentoDeFormula[],
  indice: number,
): SegmentoDeFormula[] {
  if (segmentos[indice]?.tipo !== 'variavel') return [...segmentos]
  return normalizar([...segmentos.slice(0, indice), ...segmentos.slice(indice + 1)])
}

/**
 * Validação do EDITOR: antes da gramática, recusa identificador digitado à mão
 * no texto livre. Entre os chips só cabem números, operadores, parênteses e
 * espaços — variável entra pelo botão "adicionar à fórmula".
 */
export function validarSegmentos(
  segmentos: readonly SegmentoDeFormula[],
  tags: Iterable<string>,
): ResultadoDaValidacao {
  const declaradas = new Set(tags)
  for (const segmento of segmentos) {
    if (segmento.tipo === 'variavel' && !declaradas.has(segmento.tag)) {
      return {
        ok: false,
        mensagem: `A variável "${segmento.tag}" está na fórmula mas não está declarada.`,
        posicao: 0,
      }
    }
    if (segmento.tipo === 'texto') {
      const letra = /[A-Za-z_][A-Za-z0-9_]*/.exec(segmento.valor)
      if (letra && !/[\d.]/.test(segmento.valor[letra.index - 1] ?? '')) {
        return {
          ok: false,
          mensagem: `"${letra[0]}" foi digitado na fórmula — variáveis entram pelo botão "adicionar à fórmula" da tabela de variáveis.`,
          posicao: 0,
        }
      }
    }
  }
  return validarFormula(serializar(segmentos), declaradas)
}
